import React, { useEffect, useState } from 'react';
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Container,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
  Stack,
  CircularProgress,
  Paper,
  Snackbar,
  Alert,
  Tooltip
} from '@mui/material';

import AddIcon from '@mui/icons-material/PlaylistAdd';
import EditIcon from '@mui/icons-material/ModeEdit';
import UploadIcon from '@mui/icons-material/FileUpload';
import DownloadIcon from '@mui/icons-material/FileDownload';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import PaidIcon from '@mui/icons-material/Paid';

import { useMutation, useLazyQuery } from '@apollo/client';
//import PricelistImportDialog from './PricelistImportDialog';
//import PricelistDeleteDialog from './PricelistDeleteDialog';
import PriceModelEditModal from './PriceModelEditModal';
import { getAdminSelectedCompanyId } from "../../../helpers/adminSelectors";

import { GET_PRICE_MODELS } from '../../../graphql/queries/CompanyAdminContext';
//import { DELETE_PRICELIST } from '../../../graphql/mutations';

const PriceModels = () => {

  const intl = useIntl();
  const companyId = useSelector(getAdminSelectedCompanyId);

  const [priceModels, setPriceModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  const [selectedPl, setSelectedPl] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const [actionLoading, setActionLoading] = useState(false);

  //
  // LOAD PriceModels
  //
  const [loadPriceModels, { refetch }] = useLazyQuery(GET_PRICE_MODELS, {
    variables: { companyId },
    onCompleted: (d) => {
      const lists = d?.companyAdminContext?.pricingModels?.pricingModels || [];
      setPriceModels(lists);
      setLoading(false);
    },
    onError: (err) => {
      console.error(err);
      setErrorMsg(intl.formatMessage({ id: "admin.pricemodels.loadingError" }));
      setLoading(false);
    },
  });

  useEffect(() => {
    if (companyId) {
      setLoading(true);
      loadPriceModels();
    }
  }, [companyId]);



  
  const openDelete = (pl) => {
    setSelectedPl(pl);
    setDeleteOpen(true);
  };

  //
  // DELETE MUTATION
  //
  /*const [deletePriceList] = useMutation(DELETE_PRICELIST);



  const confirmDelete = async () => {
    if (!selectedPl) return;
    setActionLoading(true);
    try {
      const { data } = await deletePriceList({
        variables: { articlePriceListId: selectedPl.articlePriceListId }
      });
      setActionLoading(false);
      setDeleteOpen(false);

      const result = data?.companyAdminContext?.pricelists?.deletePricelist;
      if (result?.success) {
        setPriceLists((prev) => prev.filter(p => p.articlePriceListId !== selectedPl.articlePriceListId));
      } else {
        setErrorMsg(intl.formatMessage({ id: "admin.pricelists.deleteError" }));
      }
    } catch (err) {
      console.error(err);
      setActionLoading(false);
      setDeleteOpen(false);
      setErrorMsg(intl.formatMessage({ id: "admin.pricelists.deleteError" }));
    }
  };*/


  //
  // EDIT
  //
  const openEdit = (pl = null) => {
    
    setSelectedPl(pl);
    setEditOpen(true);
  };

  const handleSaved = () => {
    refetch();
    setEditOpen(false);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openEdit(null)} // open modal for new pricelist
          >
            <FormattedMessage id="admin.pricemodels.add" />
          </Button>
        </Box>
      </Box>

      <Paper>
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : priceModels.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <UploadIcon sx={{ fontSize: 48 }} />
              <Typography variant="h6"><FormattedMessage id="admin.pricemodels.empty.title" /></Typography>
            <Typography sx={{ mb: 2 }}>
                <FormattedMessage id="admin.pricemodels.empty.description" />
            </Typography>
              <Button variant="contained" onClick={() => openEdit(null)}><FormattedMessage id="admin.pricemodels.add" /></Button>
          </Box>
        ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><FormattedMessage id="admin.pricemodels.table.name" /></TableCell>
                    <TableCell><FormattedMessage id="admin.pricemodels.table.description" /></TableCell>
                    <TableCell><FormattedMessage id="admin.pricemodels.table.actions" /></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {priceModels.map((pl) => (
                    <TableRow key={pl.id}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          {pl.name}
                        </Box>
                      </TableCell>
                      <TableCell>{pl.description}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <IconButton onClick={() => openEdit(pl)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => openDelete(pl)}>
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
      </Paper>

      {/* DELETE DIALOG 
      <PricelistDeleteDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        pricelistName={selectedPl?.name}
        onConfirm={confirmDelete}
        loading={actionLoading}
      />*/}

      {/* EDIT MODAL  */}
      <PriceModelEditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        pricingModelId={selectedPl?.id}
        companyId={companyId}
        onSaved={handleSaved}
      />

      {/* ERROR SNACKBAR */}
      <Snackbar open={!!errorMsg} autoHideDuration={4000} onClose={() => setErrorMsg('')}>
        <Alert severity="error">{errorMsg}</Alert>
      </Snackbar>
    </Container>
  );
};

export default PriceModels;
